import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Video, RefreshCw, AlertTriangle, ImagePlus, ExternalLink } from 'lucide-react';
import { CAMERA_MAX_SECONDS, formatClock } from '@/utils/storySegments';
import { tr } from '@/contexts/LanguageContext';

const MAX_VIDEO_SECONDS = CAMERA_MAX_SECONDS; // 60 s — single source of truth


// Iter 132-H — Detect a sandboxed-iframe scenario where the
// Permissions-Policy blocks the camera before getUserMedia() even
// tries (e.g. the Emergent preview shell at app.emergent.sh).
// When this is true we DON'T fire the failing prompt — we go
// straight to the native <input capture> fallback so the user
// never sees a confusing "permission denied" toast.
const detectFeaturePolicyBlock = () => {
  try {
    if (typeof window === 'undefined') return false;
    const inIframe = window.self !== window.top;
    if (!inIframe) return false;
    // featurePolicy is the legacy name, permissionsPolicy the modern one.
    const fp = document.featurePolicy || document.permissionsPolicy;
    if (fp?.allowsFeature) {
      return !fp.allowsFeature('camera');
    }
    return false;
  } catch {
    // Cross-origin parent access throws — assume blocked to be safe.
    return true;
  }
};

const detectMobileUA = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

/**
 * CameraCaptureView — direct camera capture for Stories.
 *
 *   · Asks for `getUserMedia({ video, audio })` on mount.
 *   · Live preview fills the host frame (object-cover).
 *   · One BIG button:
 *       · Quick tap        = photo (canvas → JPEG File).
 *       · Long-press hold  = video recording (MediaRecorder, auto-stops
 *                            at MAX_VIDEO_SECONDS or on release).
 *   · Flip camera (front/back) via `facingMode` toggle.
 *   · Honest fallback when no permission / no API → renders a "Usar
 *     galería" button that lifts the user back to the file picker.
 *   · Cleans up the MediaStream on unmount so the camera light goes off.
 *
 * Output:
 *   · onCapture(file) is fired with a real File the parent can plug
 *     straight into the existing uploadsApi.uploadMedia flow.
 */
export const CameraCaptureView = ({ onCapture, onFallback, fullscreen = false }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const holdTimerRef = useRef(null);
  const recordTimerRef = useRef(null);
  const recordChunksRef = useRef([]);
  // Iter 132-H — native <input capture> fallback. Always available
  // so the user can hit the OS camera UI even when getUserMedia is
  // blocked by an ancestor iframe's Permissions-Policy.
  const nativeCaptureRef = useRef(null);
  const nativeVideoRef   = useRef(null);

  const [facing, setFacing] = useState('user'); // 'user' | 'environment'
  // Permission state machine — broadened in Iter 132-H to render
  // contextual fallbacks instead of a single dead-end banner:
  //   'requesting'     — initial, asking the device
  //   'granted'        — stream live
  //   'denied'         — user (or OS) said no
  //   'unavailable'    — browser lacks getUserMedia entirely
  //   'iframe-blocked' — Permissions-Policy on the parent frame
  //                      forbids the camera (Emergent preview shell)
  //   'no-device'      — physical camera absent / OverconstrainedError
  //   'busy'           — camera in use by another app (NotReadable)
  const [permission, setPermission] = useState(
    detectFeaturePolicyBlock() ? 'iframe-blocked' : 'requesting'
  );
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [reachedMax, setReachedMax] = useState(false);
  const isMobile = detectMobileUA();

  // ── Acquire / release the media stream ──
  // Constraints rationale (Mar 2026):
  //   · `aspectRatio: 9/16` requests a portrait stream — phones rotate
  //     natively when the user holds the device vertically. Desktop
  //     webcams ignore this and return their landscape native ratio;
  //     we render with `object-contain` (no `object-cover`) so the live
  //     preview NEVER over-zooms in that case — the user sees the
  //     true sensor frame letterboxed inside the 9:16 viewfinder.
  //   · `width.ideal: 1920` + `height.ideal: 1080` — the browser picks
  //     the closest the sensor supports. `min` floor is a safety net so
  //     we never accept VGA on a capable device.
  //   · `frameRate.ideal: 30` — smoother capture for video stories.
  //   · `audio: true` — needed for video recording. Photos ignore it.
  const startStream = useCallback(async (facingMode) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setPermission('unavailable');
      return;
    }
    const tryConstraints = [
      // Best — full 1080p + portrait hint + 30 fps
      {
        video: {
          facingMode,
          width:  { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720  },
          aspectRatio: { ideal: 9 / 16 },
          frameRate:   { ideal: 30 },
        },
        audio: true,
      },
      // Mid — 720p drop, still portrait hint
      {
        video: {
          facingMode,
          width:  { ideal: 1280 },
          height: { ideal: 720  },
          aspectRatio: { ideal: 9 / 16 },
        },
        audio: true,
      },
      // Last resort — just facingMode, let the device pick everything.
      { video: { facingMode }, audio: true },
    ];

    let stream = null;
    let lastErr = null;
    for (const c of tryConstraints) {
      try {
        stream = await navigator.mediaDevices.getUserMedia(c);
        break;
      } catch (err) {
        lastErr = err;
      }
    }
    if (!stream) {
      console.warn('camera getUserMedia failed:', lastErr?.name, lastErr?.message);
      // Iter 132-H — granular error classification so the fallback UI
      // shows the right call-to-action (native capture / unlock perm /
      // open new tab) instead of a single dead-end banner.
      const name = lastErr?.name || '';
      if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'DevicesNotFoundError') {
        setPermission('no-device');
      } else if (name === 'NotReadableError' || name === 'TrackStartError') {
        setPermission('busy');
      } else if (detectFeaturePolicyBlock()) {
        setPermission('iframe-blocked');
      } else {
        setPermission('denied');
      }
      return;
    }
    // Stop the previous stream first (when flipping camera).
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
    streamRef.current = stream;
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      try { await videoRef.current.play(); } catch { /* iOS may defer until paint */ }
    }
    setPermission('granted');
  }, []);

  // Boots the camera when the tab mounts; tears the stream down on
  // unmount + when the facing direction changes. Iter 132-H — when we
  // already know the camera is blocked by the parent iframe's
  // Permissions-Policy we DON'T fire getUserMedia (would just spam a
  // confusing "permission denied" prompt) — the native <input capture>
  // fallback is rendered instead.
  useEffect(() => {
    if (permission !== 'iframe-blocked') {
      startStream(facing);
    }
    return () => {
      // Iter 132-I — airtight teardown: kill the live track, abort any
      // recorder mid-take, clear hold + recording timers so the hybrid
      // shutter cannot leak into the mobile main thread after unmount.
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      const rec = recorderRef.current;
      if (rec && rec.state !== 'inactive') {
        try { rec.stop(); } catch { /* noop */ }
      }
      recorderRef.current = null;
      recordChunksRef.current = [];
      if (recordTimerRef.current) {
        clearInterval(recordTimerRef.current);
        recordTimerRef.current = null;
      }
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facing]);

  // ── Photo capture via canvas ──
  const capturePhoto = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || !v.videoWidth) return;
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    const ctx = c.getContext('2d');
    // Front camera is mirrored on screen for selfie feel; flip back for the
    // saved photo so it matches what the user sees.
    if (facing === 'user') {
      ctx.translate(c.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `story-photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
      onCapture?.(file);
    }, 'image/jpeg', 0.92);
  };

  // ── Video recording via MediaRecorder ──
  // Quality knobs:
  //   · mimeType picker prefers H.264/AAC in mp4 (Safari) → falls back
  //     to VP9 webm → VP8 webm → browser default.
  //   · `videoBitsPerSecond: 4_000_000` (~4 Mbps) — high-quality 1080p
  //     without bloating short clips. The browser will under-shoot
  //     gracefully on low-end devices.
  const VIDEO_BITS_PER_SECOND = 4_000_000;
  const AUDIO_BITS_PER_SECOND = 128_000;

  const pickMimeType = () => {
    const candidates = [
      'video/mp4;codecs=h264,aac',
      'video/mp4',
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
    ];
    return candidates.find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || '';
  };

  const startRecording = () => {
    if (!streamRef.current || !window.MediaRecorder) return;
    const mimeType = pickMimeType();
    const opts = {
      videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
      audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
    };
    if (mimeType) opts.mimeType = mimeType;
    let rec;
    try {
      rec = new MediaRecorder(streamRef.current, opts);
    } catch {
      // Some browsers reject the bitrate hint — retry plain.
      try { rec = mimeType
        ? new MediaRecorder(streamRef.current, { mimeType })
        : new MediaRecorder(streamRef.current);
      } catch { return; }
    }
    recordChunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data?.size) recordChunksRef.current.push(e.data); };
    rec.onstop = () => {
      // Iter 132-I — Strip the `;codecs=...` suffix from the recorder
      // mime before handing the File to the upload validator. Browsers
      // hand us strings like `video/mp4;codecs=h264,aac` or
      // `video/webm;codecs=vp9,opus`; `uploadsApi.validateFile` only
      // whitelists the bare type (video/mp4, video/webm, video/quicktime).
      // Without this normalisation a perfectly valid recording is
      // rejected as "Formato no soportado" before `setStep('preview')`.
      const rawMime  = rec.mimeType || 'video/mp4';
      const baseMime = rawMime.split(';')[0].trim() || 'video/mp4';
      const blob = new Blob(recordChunksRef.current, { type: baseMime });
      const ext  = baseMime.includes('mp4') ? 'mp4' : 'webm';
      const file = new File([blob], `story-video-${Date.now()}.${ext}`, { type: baseMime });
      onCapture?.(file);
    };
    rec.start();
    recorderRef.current = rec;
    setRecording(true);
    setElapsed(0);
    recordTimerRef.current = setInterval(() => {
      setElapsed((s) => {
        const next = s + 0.1;
        if (next >= MAX_VIDEO_SECONDS) {
          stopRecording();
          setReachedMax(true);
          // Hide the "max alcanzado" banner after a short while so the
          // viewfinder isn't permanently capped — the user can record
          // another clip or accept the one they just made.
          setTimeout(() => setReachedMax(false), 2800);
          return MAX_VIDEO_SECONDS;
        }
        return next;
      });
    }, 100);
  };

  const stopRecording = () => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try { rec.stop(); } catch { /* noop */ }
    }
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    setRecording(false);
  };

  // ── Big button gestures ──
  // Iter 132-I founder mandate — Instagram-style hybrid shutter:
  //   • tap < 500 ms  → takePhoto()
  //   • hold ≥ 500 ms → startVideoRecording() + peripheral progress
  //                     ring (institutional #D4AF37 gold).
  //   • release       → stopVideoRecording() and the parent transitions
  //                     to the preview step via onCapture(file).
  const HOLD_MS = 500;
  const handlePointerDown = () => {
    holdTimerRef.current = setTimeout(() => {
      holdTimerRef.current = null;
      startRecording();
    }, HOLD_MS);
  };
  const handlePointerUp = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
      capturePhoto();
      return;
    }
    if (recording) stopRecording();
  };
  const handlePointerCancel = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (recording) stopRecording();
  };

  // Native OS-camera fallback. Used in two ways:
  //   • Photo path  — type="file" accept="image/*" capture="environment"
  //   • Video path  — type="file" accept="video/*" capture="environment"
  // Both open the device's native camera UI on iOS Safari / Android
  // Chrome — even when the wrapping iframe blocks getUserMedia. On
  // desktop browsers `capture` is ignored and the file picker opens.
  const triggerNativePhoto = () => nativeCaptureRef.current?.click();
  const triggerNativeVideo = () => nativeVideoRef.current?.click();
  const onNativeFileChange = (e) => {
    const f = e.target?.files?.[0];
    if (f) onCapture?.(f);
    if (e.target) e.target.value = '';
  };
  const openInNewTab = () => {
    try {
      window.open(window.location.href, '_blank', 'noopener');
    } catch {
      /* popup blockers — silent */
    }
  };

  // ── Render ──
  // Iter 132-H — granular fallback UI per permission state. Every
  // branch ALWAYS exposes a way forward: native OS-camera capture
  // (works inside the Emergent preview shell) AND the gallery.
  if (permission !== 'requesting' && permission !== 'granted') {
    const COPY = {
      'iframe-blocked': {
        title:    tr("La cámara directa está restringida por el preview."),
        subtitle: tr("Usa la cámara nativa de tu teléfono o sube desde tu galería."),
        showOpenInNewTab: true,
      },
      denied: {
        title:    tr("No tenemos permiso para usar tu cámara."),
        subtitle: isMobile
          ? tr("Activa el permiso desde Ajustes > Safari/Chrome > Cámara, o usa tu cámara nativa.")
          : tr("Permite la cámara desde el candado de la barra de direcciones o usa tu galería."),
        showOpenInNewTab: false,
      },
      unavailable: {
        title:    tr("Tu navegador no soporta cámara directa."),
        subtitle: tr("Usa la cámara nativa de tu dispositivo o tu galería."),
        showOpenInNewTab: false,
      },
      'no-device': {
        title:    tr("No detectamos una cámara conectada."),
        subtitle: tr("Sube una historia desde tu galería en su lugar."),
        showOpenInNewTab: false,
      },
      busy: {
        title:    tr("Tu cámara está siendo usada por otra aplicación."),
        subtitle: tr("Cierra esa aplicación y vuelve a intentarlo, o usa tu galería."),
        showOpenInNewTab: false,
      },
    };
    const copy = COPY[permission] || COPY.denied;
    return (
      <div
        className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 bg-black"
        data-testid={`camera-permission-${permission}`}
      >
        <AlertTriangle className="w-10 h-10 text-amber-400 mb-3" />
        <p className="text-sm font-semibold text-white mb-1 max-w-[280px] leading-snug">
          {copy.title}
        </p>
        <p className="text-xs text-white/70 mb-5 max-w-[300px] leading-snug">
          {copy.subtitle}
        </p>

        {/* Hidden native inputs — clicking these opens the OS camera
            UI even from inside a sandboxed iframe. */}
        <input
          ref={nativeCaptureRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onNativeFileChange}
          data-testid="camera-native-photo-input"
        />
        <input
          ref={nativeVideoRef}
          type="file"
          accept="video/*"
          capture="environment"
          className="hidden"
          onChange={onNativeFileChange}
          data-testid="camera-native-video-input"
        />

        <div className="flex flex-col items-stretch gap-2 w-full max-w-[260px]">
          {/* Primary CTAs — native OS camera (foto + video). */}
          <button
            type="button"
            onClick={triggerNativePhoto}
            data-testid="camera-native-photo-btn"
            className="inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-full bg-primary text-primary-foreground text-sm font-semibold"
          >
            <Camera className="w-4 h-4" />{tr("Tomar foto con cámara nativa")}</button>
          <button
            type="button"
            onClick={triggerNativeVideo}
            data-testid="camera-native-video-btn"
            className="inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-full bg-white/10 text-white text-sm font-semibold border border-white/20 hover:bg-white/15"
          >
            <Video className="w-4 h-4" />{tr("Grabar video nativo")}</button>

          {/* Secondary — back to gallery. */}
          <button
            type="button"
            onClick={onFallback}
            data-testid="camera-fallback-btn"
            className="inline-flex items-center justify-center gap-1.5 h-9 px-4 mt-1 rounded-full bg-white/5 text-white/80 text-xs font-medium border border-white/10 hover:bg-white/10"
          >
            <ImagePlus className="w-3.5 h-3.5" />{tr("Subir desde galería")}</button>

          {/* Tertiary — open in new tab (iframe-blocked only). */}
          {copy.showOpenInNewTab && (
            <button
              type="button"
              onClick={openInNewTab}
              data-testid="camera-open-tab-btn"
              className="inline-flex items-center justify-center gap-1.5 h-8 px-3 mt-1 text-[11px] font-medium text-amber-200/80 hover:text-amber-100"
            >
              <ExternalLink className="w-3 h-3" />{tr("Abrir Ziyup en una pestaña nueva")}</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        // Iter 132-J — IG-style fullscreen.
        //
        // When `fullscreen` is on (the new StoryUploadDialog mounts the
        // viewfinder edge-to-edge), we want the preview to BLEED across
        // the whole pane like Instagram does — `object-cover` crops the
        // narrowest axis. Mobile phones return a native 9:16 portrait
        // stream so no real cropping happens; on a desktop 16:9 webcam
        // the sides are trimmed (acceptable — we optimise for mobile).
        //
        // When `fullscreen` is off (legacy boxed dialog) we keep
        // `object-contain` so a desktop webcam never zooms in — the
        // letterbox is the lesser evil compared to a severely zoomed
        // crop on a sensor that doesn't match 9:16.
        className={`w-full h-full ${fullscreen ? 'object-cover' : 'object-contain'} bg-black ${facing === 'user' ? 'scale-x-[-1]' : ''}`}
        data-testid="camera-preview-video"
      />
      <canvas ref={canvasRef} className="hidden" aria-hidden />

      {/* Linear progress bar at the very top — IG-style.
          Always rendered (faint) so the cap is communicated even at 0s.
          Animates to 100% as recording approaches MAX_VIDEO_SECONDS.
          Iter 132-I — bar uses the institutional gold token. */}
      <div
        className="absolute top-0 inset-x-0 h-1 bg-white/10 z-30 pointer-events-none"
        data-testid="camera-recording-progress"
      >
        <motion.div
          className="h-full origin-left"
          style={{ backgroundColor: '#D4AF37' }}
          initial={false}
          animate={{ width: `${Math.min(100, (elapsed / MAX_VIDEO_SECONDS) * 100)}%` }}
          transition={{ ease: 'linear', duration: 0.1 }}
        />
      </div>

      {/* "Has alcanzado el máximo" banner — pulses briefly after auto-stop. */}
      <AnimatePresence>
        {reachedMax && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="absolute top-2 left-1/2 -translate-x-1/2 z-40 inline-flex items-center gap-1.5 bg-amber-500/95 text-amber-950 text-[11px] font-bold px-3 py-1.5 rounded-full shadow-lg"
            data-testid="camera-max-reached-banner"
          >
            <AlertTriangle className="w-3.5 h-3.5" />{tr("Has alcanzado el máximo de la cámara")}</motion.div>
        )}
      </AnimatePresence>

      {/* Top-right flip camera (only if multiple cameras likely available) */}
      <button
        type="button"
        onClick={() => setFacing((f) => (f === 'user' ? 'environment' : 'user'))}
        aria-label={tr("Cambiar cámara")}
        data-testid="camera-flip-btn"
        className="absolute top-3 right-3 z-30 w-9 h-9 rounded-full bg-black/55 backdrop-blur-md border border-white/15 flex items-center justify-center text-white hover:bg-black/75"
      >
        <RefreshCw className="w-4 h-4" />
      </button>

      {/* Recording timer pill — shows elapsed + remaining (IG style) */}
      <AnimatePresence>
        {recording && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute top-3 left-1/2 -translate-x-1/2 z-30 inline-flex items-center gap-2 bg-destructive/85 backdrop-blur-md text-white text-xs font-bold px-3 py-1.5 rounded-full"
            data-testid="camera-recording-timer"
          >
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
            <span className="font-mono tabular-nums">{formatClock(elapsed)}</span>
            <span className="text-white/70 font-mono tabular-nums text-[10px]">
              / {formatClock(MAX_VIDEO_SECONDS)}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Big capture button — bottom-center · Iter 132-I hybrid shutter */}
      <div className="absolute bottom-5 inset-x-0 flex items-center justify-center z-30">
        <div className="relative">
          {/* Outer ring fills institutional gold while recording. The
              dasharray progresses linearly toward 100% over 60 s. */}
          {recording && (
            <svg
              className="absolute -inset-1 w-[88px] h-[88px] -rotate-90 pointer-events-none"
              viewBox="0 0 100 100"
              data-testid="camera-recording-ring"
            >
              <circle
                cx="50" cy="50" r="46"
                fill="none" stroke="rgba(255,255,255,.25)" strokeWidth="4"
              />
              <circle
                cx="50" cy="50" r="46"
                fill="none" stroke="#D4AF37" strokeWidth="4" strokeLinecap="round"
                strokeDasharray={`${(elapsed / MAX_VIDEO_SECONDS) * 289} 289`}
              />
            </svg>
          )}
          <button
            type="button"
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerCancel}
            onPointerCancel={handlePointerCancel}
            data-testid="camera-capture-btn"
            data-recording={recording ? 'true' : 'false'}
            aria-label={tr("Capturar (tap = foto, mantén ≥ 500 ms = video)")}
            className={`relative w-20 h-20 rounded-full border-4 border-white flex items-center justify-center transition-colors ${
              recording ? 'bg-destructive' : 'bg-white/15 active:bg-white/30'
            }`}
          >
            {recording ? (
              <Video className="w-8 h-8 text-white" strokeWidth={1.5} />
            ) : (
              <Camera className="w-8 h-8 text-white" strokeWidth={1.5} />
            )}
          </button>
        </div>
      </div>

      {/* Hint line */}
      <div className="absolute bottom-1 inset-x-0 text-center text-[10px] text-white/70 z-20 pointer-events-none">{tr("Toca para foto · Mantén para grabar (máx {0})", formatClock(MAX_VIDEO_SECONDS))}</div>
    </>
  );
};

export default CameraCaptureView;
